import React, { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Switch, Button, TextField, Typography, Box
} from '@mui/material';
import { Save, SettingsSuggest } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../../api/axios';

// Matches your backend SystemSetting entity/DTO
interface SystemSetting {
  settingType: string;
  disabled: boolean;
  disabledUntil: string | null;
}

const SystemSettings = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/system/settings');
      setSettings(res.data);
    } catch (err) {
      toast.error('Failed to load system settings');
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleToggle = (index: number) => {
    const newSettings = [...settings];
    newSettings[index].disabled = !newSettings[index].disabled;
    setSettings(newSettings);
  };

  const handleDateChange = (index: number, date: string) => {
    const newSettings = [...settings];
    // Backend expects ISO or null
    newSettings[index].disabledUntil = date ? new Date(date).toISOString() : null;
    setSettings(newSettings);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.post('/system/settings', settings);
      toast.success('System configuration updated');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
      <Paper className="p-6 bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-800 shadow-2xl rounded-2xl">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <Box>
            <div className="flex items-center gap-2 mb-1">
              <SettingsSuggest className="text-blue-500" />
              <Typography className="text-xl font-bold text-gray-800 dark:text-neutral-100">
                Global Feature Flags
              </Typography>
            </div>
            <p className="text-xs text-gray-500 font-medium tracking-tight">
              Control core system modules and maintenance windows
            </p>
          </Box>
          <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={loading}
              className="bg-dark-primary hover:bg-opacity-90 h-10 px-8 rounded-xl font-bold shadow-lg shadow-blue-500/20"
          >
            Save Changes
          </Button>
        </div>

        {/* Added max-h and scrollbar-thin for better layout management */}
        <TableContainer
            className="rounded-xl border border-gray-50 dark:border-neutral-800 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-neutral-700 scrollbar-track-transparent"
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="bg-gray-50/80 dark:bg-neutral-800/80 backdrop-blur font-bold text-xs uppercase tracking-widest border-b dark:border-neutral-700">
                  System Feature
                </TableCell>
                <TableCell align="center" className="bg-gray-50/80 dark:bg-neutral-800/80 backdrop-blur font-bold text-xs uppercase tracking-widest border-b dark:border-neutral-700">
                  Status
                </TableCell>
                <TableCell className="bg-gray-50/80 dark:bg-neutral-800/80 backdrop-blur font-bold text-xs uppercase tracking-widest border-b dark:border-neutral-700">
                  Disabled Until (Optional)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.map((s, idx) => (
                  <TableRow key={s.settingType} hover className="transition-colors group">
                    <TableCell className="py-4 border-b dark:border-neutral-800">
                  <span className="font-mono font-bold text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                    {s.settingType}
                  </span>
                    </TableCell>
                    <TableCell align="center" className="border-b dark:border-neutral-800">
                      <div className="flex flex-col items-center">
                        <Switch
                            checked={s.disabled}
                            onChange={() => handleToggle(idx)}
                            color="error"
                        />
                        {/* Changed labels to Enabled/Disabled for clearer semantics */}
                        <span className={`text-[10px] font-black uppercase tracking-tighter ${s.disabled ? 'text-red-500' : 'text-emerald-500'}`}>
                      {s.disabled ? 'Disabled' : 'Enabled'}
                    </span>
                      </div>
                    </TableCell>
                    <TableCell className="border-b dark:border-neutral-800">
                      <TextField
                          type="datetime-local"
                          size="small"
                          value={s.disabledUntil ? s.disabledUntil.substring(0, 16) : ''}
                          onChange={(e) => handleDateChange(idx, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          className="max-w-[220px]"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              backgroundColor: 'rgba(0,0,0,0.02)',
                              '&.Mui-focused fieldset': { borderWidth: '1px' }
                            }
                          }}
                      />
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
  );
};

export default SystemSettings;